import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost } from "../api/client";
import { usePlannerStore } from "../state/usePlannerStore";

export type RoadmapFilter =
  | "unplanned"
  | "deferred"
  | "recurring";

export interface RoadmapEntry {
  id: string;
  xi_entry_id: string;
  company_id: string;
  owner_user_id: string;
  title: string;
  intent: string;
  signal_lineage: Record<string, unknown>;
  status: string;
  projected_outcome: Record<string, unknown>;
  target_window_start: string | null;
  target_window_end: string | null;
  promoted_at: string;
  last_synced_at: string;
  deferred_until: string | null;
  deferred_reason: string | null;
  // XI canonical domain — null only on legacy rows that pre-date the v1
  // domain migration. New rows always carry a domain (derived from XTS
  // initiative_type or set explicitly when local).
  domain: string | null;
  // 'one_shot' | 'recurrence_template' | 'recurrence_instance'. v1.5 prep.
  entry_kind: string;
  // Per-V1.5 spec — null today; populated by XI scheduler when v1.5 ships.
  recurrence?: Record<string, unknown> | null;
  parent_entry_id?: string | null;
  // Non-null when a draft/approved/running play already exists for this entry
  // in the current workspace. Set by the list endpoint via LATERAL subquery.
  active_play_id?: string | null;
  // Advisory recommendation from the Inbox AI Assessment classifier.
  // Null when the entry hasn't been classified yet (e.g. XTS-federated
  // rows that haven't gone through the local promote bus, or before the
  // worker fires post-promote).
  ai_classification?: AiClassification | null;
}

export interface AiClassification {
  recommendation: "play" | "gameplan";
  confidence: number;
  reasoning: string;
  signals: {
    archetype: string | null;
    cross_team: string | null;
    multi_workstream_hint: boolean;
    target_window_days: number | null;
  };
  model: string;
  evaluated_at: string;
  evaluated_by: "auto" | "manual";
}

export interface RoadmapListResponse {
  entries: RoadmapEntry[];
  total: number;
  limit: number;
  offset: number;
}

export function statusFilterFor(filter: RoadmapFilter): string {
  switch (filter) {
    case "unplanned":
      // Truly unplanned — committed entry, no play attached at all.
      // Once a play exists, the entry leaves the Inbox entirely and
      // appears on the Plays page (as Planning or In flight, by play
      // state). The Inbox is the decision queue; the Plays page is
      // where post-decision work lives.
      return "committed";
    case "deferred":
      return "deferred";
    case "recurring":
      // Recurring tab spans every status — both pending instances awaiting
      // Plan-it (committed) and previously-actioned ones (in_execution / etc).
      return "committed,in_execution,completed,abandoned,deferred";
  }
}

/**
 * has_play filter: Inbox is the decision queue — only Unplanned
 * applies, and it explicitly excludes entries that have any non-
 * terminal play. Deferred + Recurring don't filter on play state
 * (their semantics are independent of play existence).
 */
export function hasPlayFilterFor(filter: RoadmapFilter): "yes" | "no" | null {
  if (filter === "unplanned") return "no";
  return null;
}

/**
 * v1.5: each filter implies a default entry_kind set. Unplanned/Deferred/
 * Planned tabs default-exclude recurrence_template (templates aren't
 * actionable from the inbox; the Recurring tab opts back in).
 */
export function entryKindFilterFor(filter: RoadmapFilter): string | null {
  if (filter === "recurring") return "recurrence_template,recurrence_instance";
  return null; // server default: one_shot + recurrence_instance
}

export function useRoadmapEntries(
  workspaceId: string | null | undefined,
  filter: RoadmapFilter,
  domain?: string | null,
) {
  const status = statusFilterFor(filter);
  const entryKind = entryKindFilterFor(filter);
  return useQuery<RoadmapListResponse, Error>({
    queryKey: ["roadmap", workspaceId, filter, domain ?? "all"],
    enabled: !!workspaceId,
    queryFn: () => {
      const qs = new URLSearchParams({ status });
      if (domain) qs.set("domain", domain);
      if (entryKind) qs.set("entry_kind", entryKind);
      const hasPlay = hasPlayFilterFor(filter);
      if (hasPlay) qs.set("has_play", hasPlay);
      return apiGet<RoadmapListResponse>(
        `/api/workspaces/${workspaceId}/roadmap?${qs.toString()}`,
      );
    },
  });
}

export interface DeferEntryArgs {
  entryId: string;
  reason: string;
  deferred_until?: string;
}

export function useAssessEntry(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<AiClassification, Error, string>({
    mutationFn: async (entryId) => {
      const r = await apiPost<{ classification: AiClassification }>(
        `/api/workspaces/${workspaceId}/roadmap/${entryId}/assess`,
        {},
      );
      return r.classification;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
    },
  });
}

export function useDeferEntry(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<{ entry: RoadmapEntry }, Error, DeferEntryArgs>({
    mutationFn: ({ entryId, reason, deferred_until }) =>
      apiPost<{ entry: RoadmapEntry }>(
        `/api/workspaces/${workspaceId}/roadmap/${entryId}/defer`,
        deferred_until ? { reason, deferred_until } : { reason },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
    },
  });
}

export interface RequestContextArgs {
  entryId: string;
  question: string;
}

export function useRequestContext(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<
    { queued: true; queue_id: string },
    Error,
    RequestContextArgs
  >({
    mutationFn: ({ entryId, question }) =>
      apiPost<{ queued: true; queue_id: string }>(
        `/api/workspaces/${workspaceId}/roadmap/${entryId}/request-context`,
        { question },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
    },
  });
}

export interface ReassignEntryArgs {
  entryId: string;
  new_owner_user_id: string;
  note?: string;
}

export function useReassignEntry(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<{ entry: RoadmapEntry }, Error, ReassignEntryArgs>({
    mutationFn: ({ entryId, new_owner_user_id, note }) =>
      apiPost<{ entry: RoadmapEntry }>(
        `/api/workspaces/${workspaceId}/roadmap/${entryId}/reassign`,
        note ? { new_owner_user_id, note } : { new_owner_user_id },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
    },
  });
}

export interface SetTemplateKeysArgs {
  entryId: string;
  industry: string;
  trade: string;
  archetype: string;
}

export function useSetTemplateKeys(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<{ entry: RoadmapEntry }, Error, SetTemplateKeysArgs>({
    mutationFn: ({ entryId, industry, trade, archetype }) =>
      apiPatch<{ entry: RoadmapEntry }>(
        `/api/workspaces/${workspaceId}/roadmap/${entryId}/template-keys`,
        { industry, trade, archetype },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
    },
  });
}

export interface PlanItArgs {
  entryId: string;
  overrideTemplateId?: string;
  boundAgents?: { id: string; capabilities: string[] }[];
  /**
   * Outcome captured on the chooser (Template / Quick Launch paths
   * that skip Discovery, or pre-filled before Discovery). Persisted
   * onto the play row so the OutcomeHeader can render it.
   */
  outcomeStatement?: string | null;
  successMetrics?: Array<{
    label: string;
    target: string;
    measurement_window?: string;
    baseline?: string;
    horizon_weeks?: number;
  }>;
}

export type PlanItResult =
  | {
      kind: "draft_play";
      play: { id: string; [k: string]: unknown };
      templateId: string;
      decompositionInputId: string;
    }
  | {
      kind: "discovery_required";
      reason: string;
      detail: string;
      sessionId?: string;
      gaps?: string[];
    };

export function usePlanIt(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<PlanItResult, Error, PlanItArgs>({
    mutationFn: ({
      entryId, overrideTemplateId, boundAgents, outcomeStatement, successMetrics,
    }) => {
      const body: Record<string, unknown> = { roadmapEntryId: entryId };
      if (overrideTemplateId !== undefined)
        body["overrideTemplateId"] = overrideTemplateId;
      if (boundAgents !== undefined) body["boundAgents"] = boundAgents;
      if (outcomeStatement !== undefined && outcomeStatement !== null)
        body["outcomeStatement"] = outcomeStatement;
      if (successMetrics !== undefined && successMetrics.length > 0)
        body["successMetrics"] = successMetrics;
      return apiPost<PlanItResult>(
        `/api/workspaces/${workspaceId}/planner/decompose`,
        body,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
    },
  });
}

export interface FromTemplateArgs {
  industry: string;
  trade: string;
  archetype: string;
  outcomeStatement?: string;
  successMetrics?: Array<{
    label: string;
    target: string;
    measurement_window?: string;
    baseline?: string;
    horizon_weeks?: number;
  }>;
}

export type FromTemplateResult =
  | {
      kind: "draft_play";
      playId: string;
      entryId: string;
      templateId: string;
      decompositionInputId: string;
      play: { id: string; [k: string]: unknown };
    }
  | {
      kind: "existing_play";
      playId: string;
      entryId: string;
      state: string;
    }
  | {
      kind: "discovery_required";
      entryId: string;
      reason: string;
      detail: string;
      gaps: string[];
    };

/**
 * One-shot template → play creation. Mints a roadmap entry from the
 * template, decomposes it, and returns the new play. Bypasses the
 * "find an inbox entry first" detour for users browsing the templates
 * catalog who already know which template they want.
 */
export function useFromTemplate(workspaceId: string) {
  const qc = useQueryClient();
  const setLoadingGlobal = usePlannerStore((s) => s.setLoadingGlobal);

  return useMutation<FromTemplateResult, Error, FromTemplateArgs>({
    onMutate: () => {
      setLoadingGlobal(true);
    },
    mutationFn: (body) =>
      apiPost<FromTemplateResult>(
        `/api/workspaces/${workspaceId}/planner/from-template`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
      qc.invalidateQueries({ queryKey: ["plays", workspaceId] });
    },
    onSettled: () => {
      setLoadingGlobal(false);
    },
  });
}

export type RecurrenceFrequency = "daily" | "weekly" | "monthly" | "quarterly" | "annually";

export interface RecurrenceConfig {
  frequency: RecurrenceFrequency;
  day_of_month?: number;
  day_of_week?: number;
  starts_on?: string;
}

export interface SplitChild {
  title: string;
  intent?: string;
  entry_kind: "one_shot" | "recurrence_template";
  recurrence?: RecurrenceConfig;
}

export interface SplitArgs {
  entryId: string;
  children: SplitChild[];
  rationale?: string;
}

export function useSplitEntry(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<{ children: RoadmapEntry[]; parentId: string }, Error, SplitArgs>({
    mutationFn: ({ entryId, children, rationale }) =>
      apiPost(`/api/workspaces/${workspaceId}/roadmap/${entryId}/split`, {
        children,
        ...(rationale ? { rationale } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
    },
  });
}

export interface SetRecurrenceArgs {
  entryId: string;
  recurrence: RecurrenceConfig;
}

export function useSetRecurrence(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<{ entry: RoadmapEntry }, Error, SetRecurrenceArgs>({
    mutationFn: ({ entryId, recurrence }) =>
      apiPatch(`/api/workspaces/${workspaceId}/roadmap/${entryId}/recurrence`, recurrence),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
    },
  });
}
